import type { ProductListItem } from '@travel-planner/shared/schemas';
import type { AdvisorProductType } from '../model/types';
import { SourceProductCard } from '@itineraries/widgets/planner-products-pane/ui/source-product-card';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { EmptyState } from '@/shared/ui/empty-state';
import { Pagination, PaginationButton } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { Text } from '@/shared/ui/text';
import { titleCaseRegionTitle } from '../model/region-layout';
import { ADVISOR_PAGE_SIZE } from '../model/to-query-input';
import css from './advisor-planner-workspace.module.scss';
import { useOptionalPlannerHost } from './planner-host';

const TYPE_LABELS: Record<AdvisorProductType, string> = {
  hotels: 'Hotels',
  experiences: 'Experiences',
  restaurants: 'Restaurants',
};

type ProductStackProps = {
  items: ProductListItem[];
  isLoading: boolean;
  isError: boolean;
  type: AdvisorProductType;
  regionTitles: string[];
  onAdd: (item: ProductListItem) => void;
  onOpenDetails: (productId: string) => void;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
};

export const ProductStack = ({
  items,
  isLoading,
  isError,
  type,
  regionTitles,
  onAdd,
  onOpenDetails,
  page,
  total,
  onPageChange,
}: ProductStackProps) => {
  // Host capability, not a prop: the parent spreads `planner.productStack`
  // into this component, so an extra prop there would have to be threaded
  // through the hook's return. The CMS agent portal host leaves this off —
  // the average is internal net cost.
  const showAvgRoomNight = useOptionalPlannerHost()?.showAvgRoomNight ?? false;
  const totalPages = Math.max(1, Math.ceil(total / ADVISOR_PAGE_SIZE));
  return (
    <section aria-label="Catalogue" className={css.productPane}>
      <Text className={css.productPaneTitle} size="xs" variant="muted">
        {TYPE_LABELS[type]}
        {regionTitles.length > 0
          ? ` · ${regionTitles.map(titleCaseRegionTitle).join(', ')}`
          : ''}
      </Text>
      {isLoading && (
        <div className={css.productList}>
          <Skeleton className={css.productSkeleton} height="76px" />
          <Skeleton className={css.productSkeleton} height="76px" />
        </div>
      )}
      {!isLoading && isError && (
        <div className={css.productEmpty}>
          <EmptyState
            description="Check your connection, then change a filter to retry."
            title="Could not load products"
          />
        </div>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <div className={css.productEmpty}>
          <EmptyState
            description="Try another region or tag — this filter uses the live catalogue."
            title="No products in this slice"
          />
        </div>
      )}
      {!isLoading && !isError && items.length > 0 && (
        <div className={css.productList} key={page}>
          {items.map((item) => (
            <div className={css.productRow} key={item.id}>
              <SourceProductCard
                onOpenDetails={() => onOpenDetails(item.id)}
                product={item}
                showAvgRoomNight={showAvgRoomNight}
              />
              <Button onClick={() => onAdd(item)} size="sm" variant="outline">
                Add
              </Button>
            </div>
          ))}
        </div>
      )}
      {!isLoading && !isError && totalPages > 1 && (
        <div className={css.productFooter}>
          <Text size="xs" variant="muted">
            Page {page} of {totalPages}
          </Text>
          <Pagination>
            <PaginationButton
              aria-label="First page"
              disabled={page <= 1}
              onClick={() => onPageChange(1)}
            >
              <ChevronsLeft style={{ height: 14, width: 14 }} />
            </PaginationButton>
            <PaginationButton
              aria-label="Previous page"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              <ChevronLeft style={{ height: 14, width: 14 }} />
            </PaginationButton>
            <PaginationButton
              aria-label="Next page"
              disabled={page >= totalPages}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              <ChevronRight style={{ height: 14, width: 14 }} />
            </PaginationButton>
            <PaginationButton
              aria-label="Last page"
              disabled={page >= totalPages}
              onClick={() => onPageChange(totalPages)}
            >
              <ChevronsRight style={{ height: 14, width: 14 }} />
            </PaginationButton>
          </Pagination>
        </div>
      )}
    </section>
  );
};
